import type { MovementRecord } from '@jmc/shared';
import { daysOverdue, formatDate, formatDateTime, MOVEMENT_PURPOSE_LABELS } from '@jmc/shared';
import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from '../lib/api.js';
import { useI18n } from '../lib/i18n.js';
import {
  cx,
  EmptyState,
  ErrorState,
  Mono,
  PageHeader,
  Panel,
  TableSkeleton,
} from '../components/ui.js';

export function IssuedPage() {
  const { t, pick } = useI18n();
  const [params, setParams] = useSearchParams();
  const onlyOverdue = params.get('overdue') === 'true';

  const query = useQuery({
    queryKey: ['movements', 'open', onlyOverdue],
    queryFn: () =>
      api<{ movements: MovementRecord[] }>(`/movements/open?overdue=${onlyOverdue}`),
  });

  const movements = query.data?.movements ?? [];

  return (
    <>
      <PageHeader
        title={t('nav.issued')}
        actions={
          <div className="flex rounded-[5px] border border-line-strong p-0.5">
            {([false, true] as const).map((value) => (
              <button
                key={String(value)}
                type="button"
                onClick={() => {
                  const next = new URLSearchParams(params);
                  if (value) next.set('overdue', 'true');
                  else next.delete('overdue');
                  setParams(next, { replace: true });
                }}
                aria-pressed={onlyOverdue === value}
                className={cx(
                  'rounded-[3px] px-3 py-1 text-[0.8125rem] font-medium transition-colors',
                  onlyOverdue === value
                    ? 'bg-navy-700 text-white'
                    : 'text-ink-500 hover:text-navy-700',
                )}
              >
                {value ? t('status.overdue') : t('common.all')}
              </button>
            ))}
          </div>
        }
      />

      <Panel bodyClassName="overflow-x-auto">
        {query.isLoading && <TableSkeleton columns={5} />}
        {query.isError && (
          <div className="p-4">
            <ErrorState error={query.error} onRetry={() => void query.refetch()} />
          </div>
        )}
        {query.isSuccess && movements.length === 0 && (
          <EmptyState
            title={onlyOverdue ? t('search.noResults') : t('search.noResults')}
            hint={onlyOverdue ? undefined : t('search.hint')}
          />
        )}

        {movements.length > 0 && (
          <table className="data-table">
            <thead>
              <tr>
                <th>{t('field.eFileNumber')}</th>
                <th>{t('field.issuedTo')}</th>
                <th className="hidden md:table-cell">{t('field.purpose')}</th>
                <th className="hidden lg:table-cell">{t('field.issuedOn')}</th>
                <th>{t('field.expectedReturn')}</th>
              </tr>
            </thead>
            <tbody>
              {movements.map((movement) => {
                const late = daysOverdue(movement.expectedReturnDate);
                return (
                  <tr key={movement.id}>
                    <td className="whitespace-nowrap">
                      <Link
                        to={`/files/${movement.fileId}`}
                        className="font-medium text-navy-600 hover:underline"
                      >
                        <Mono>{movement.eFileNumber}</Mono>
                      </Link>
                    </td>
                    <td>
                      <div className="font-medium">{movement.issuedToName}</div>
                      <div className="text-[0.8125rem] text-ink-500 tabular">
                        {movement.issuedToMobileMasked}
                      </div>
                    </td>
                    <td className="hidden md:table-cell">
                      {pick(
                        MOVEMENT_PURPOSE_LABELS[movement.purpose].en,
                        MOVEMENT_PURPOSE_LABELS[movement.purpose].hi,
                      )}
                    </td>
                    <td className="hidden whitespace-nowrap lg:table-cell">
                      {formatDateTime(movement.issuedAt)}
                    </td>
                    <td className="whitespace-nowrap">
                      <span className={cx(movement.isOverdue && 'font-medium text-amber-ink')}>
                        {formatDate(movement.expectedReturnDate)}
                      </span>
                      {movement.isOverdue && late > 0 && (
                        <div className="text-[0.75rem] text-amber-ink">
                          {late} {late === 1 ? 'day' : 'days'} {t('status.overdue').toLowerCase()}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </Panel>
    </>
  );
}
